import { Component, OnChanges, OnDestroy, OnInit } from "@angular/core"
import { Title } from "@angular/platform-browser"
import { ActivatedRoute, ParamMap, Params, Router } from "@angular/router"
import { asyncScheduler, combineLatest, Observable, Subject, Subscription } from "rxjs"
import { PremiumCollectionOfResource } from "../models/collection/premiumCollectionOfResource.model"
import { PremiumColors } from "../models/collection/premiumColors.model"
import { PBSLM_TITLE, STATIC_IMAGES } from "../models/constants"
import { ResourceGTM } from "../models/gtm/resource"
import { Resource } from "../models/resource/resource"
import { GlobalStoreService } from "../services/global-store.service"
import { GTMService } from "../services/gtm.service"
import { ResourceService } from "../services/resource.service"
import { UserService } from "../services/user.service"
import { ModalService } from "../shared-components/modal/modal.service"
import { TruncatePipe } from "../shared-components/pipes/truncate.pipe"
import ResourceUtils from "../utils/resource-utils"
import { ShareLinkModalComponent } from "../shared-components/modals/share-link-modal/share-link-modal.component"
import { ShareLinkGoogleClassModalComponent } from "../shared-components/modals/share-link-google-class-modal/share-link-google-class-modal.component"
import { SocialShareModalComponent } from "../shared-components/modals/social-share-modal/social-share-modal.component"
import { LessonBuilderService } from "../services/lesson-builder.service"
import { LMModalService } from "../services/lm-modal.service"
import { PreviousRouteService } from "../services/previous-route.service"
import { AddOnViewService } from "../services/add-on-view.service"
import Utils from "../utils/utils"
import { CautionFlagsService } from "../services/caution-flags.service"
import { IndexedDBService } from "../services/index-DB.service"
import { takeUntil, tap } from "rxjs/operators"
import { IWindowResizeEvent, WindowSizeService } from "../services/window-size.service"
import { TooltipPlacementModel } from "../models/tooltipPlacement.model"
import { PreviewService } from "../services/preview.service"
import { routeAnimations, routeTransition } from "../utils/animations"

declare var window: any

@Component({
    templateUrl: "./resource.component.html",
    styleUrls: ["./resource.component.scss"],
    host: { "[@routeTransition]": "true", "[@routeAnimations]": "false", style: "display: block;" },
    animations: [routeTransition, routeAnimations],
    standalone: false,
})
export class ResourceComponent implements OnInit, OnDestroy, OnChanges {
    resource: Resource = new Resource()
    googleClassroomImage
    project: any
    projectColors: PremiumColors = new PremiumColors({})
    premiumCollectionSlug: string
    premiumCollectionOfResource: PremiumCollectionOfResource
    shareToGCTooltip: string = "Share to Google Classroom."
    assignToGCTooltip: string = "Assign to class"
    shareWithStudentsTooltip: string = ""
    buildLessonTooltip: string = ""
    previousUrl: string
    isAddonView: boolean
    isPreviousPage: boolean
    isTeacherPreview: boolean
    shareButtonsPlacement: TooltipPlacementModel = null

    private subscriptions: Subscription[] = []
    private destroyed$: Subject<void> = new Subject<void>()
    cautionFlagDisplayObservable: Observable<boolean> =
        this.cautionFlagsService.cautionFlagDisplayObservable.pipe(
            takeUntil(this.destroyed$),
            tap((nextState: boolean) => nextState && this.scrollToCautionsTab())
        )

    windowResizeEventObservable: Observable<IWindowResizeEvent> =
        this.windowSizeService.resize.pipe(
            takeUntil(this.destroyed$),
            tap((resizeEvent: IWindowResizeEvent) =>
                this.handleShareButtonsTooltipDisplay(resizeEvent)
            )
        )

    isInPreviewMode$: Observable<boolean>

    constructor(
        public addOnService: AddOnViewService,
        public previousRouteService: PreviousRouteService,
        public resourceService: ResourceService,
        public userService: UserService,
        public activatedRoute: ActivatedRoute,
        private router: Router,
        private modalService: ModalService,
        private titleService: Title,
        private gtmService: GTMService,
        private truncatePipe: TruncatePipe,
        private globalStoreService: GlobalStoreService,
        private lessonBuilderService: LessonBuilderService,
        private lmModalService: LMModalService,
        private cautionFlagsService: CautionFlagsService,
        private indexedDBService: IndexedDBService,
        private windowSizeService: WindowSizeService,
        private previewService: PreviewService
    ) {
        this.googleClassroomImage = STATIC_IMAGES.google_classroom_x48_yellow_stroke_icon
        this.titleService.setTitle(PBSLM_TITLE)
        this.resourceService.resetData()
    }

    public ngOnInit() {
        this.handleNavigationOnPreview()
        this.isInPreviewMode$ = this.previewService.previewOnResource$
        this.windowResizeEventObservable.subscribe()
        this.isAddonView = this.addOnService.isAddonView
        this.isTeacherPreview = this.addOnService.isTeacherPreview
        this.premiumCollectionSlug = ResourceUtils.getPremiumCollectionSlug()
        if (this.isAddonView && this.userService.isStudent()) {
            this.previousUrl = window.location.pathname
        }

        this.activatedRoute.paramMap.subscribe((paramMap: ParamMap) => {
            const resourceCode: string = paramMap.get("resourceCode")
            this.resourceService.getResource(resourceCode)
        })

        this.activatedRoute.queryParamMap.subscribe((queryParamMap: ParamMap) => {
            this.resourceService.focusView =
                queryParamMap.get("focus") === "true" && queryParamMap.get("student") === "true"

            if (queryParamMap.get("focus") === "true" && !queryParamMap.get("student")) {
                return this.resourceService.switchResourceView()
            }
        })

        this.subscriptions.push(
            this.previousRouteService.currentUrl$.subscribe((value: string) => {
                this.previousUrl = value
                this.isPreviousPage =
                    this.isAddonView &&
                    !this.userService.isStudent() &&
                    Utils.isAddOnExperienceLink(this.previousUrl, "/add-on/search")
            })
        )

        this.subscriptions.push(
            this.resourceService.resourceResponse.subscribe((resource: Resource) => {
                this.findPremiumCollectionOfResource(resource)
                const oldResourceCode: string = this.resource.resourceCode
                this.resource = resource
                this.previewService.resource = resource
                this.project = this.resource.project
                if (this.project && this.project.style_content) {
                    this.projectColors = new PremiumColors(this.project.style_content)
                }
                this.updateTooltips()
                this.globalStoreService.currentResource = this.resource
                this.checkResourceSkipCautionState()
                if (this.resource.isLoaded) {
                    if (!this.resource.opensAsVideo && this.resourceService.focusView) {
                        this.resourceService.switchResourceView(true)
                    }
                    this.titleService.setTitle(this.makeTitle())
                    if (this.resource.resourceCode !== oldResourceCode) {
                        this.gtmService.setDataLayer(new ResourceGTM(this.resource))
                        this.gtmService.pushPageView()
                        this.resourceService.incrementViewCount(this.resource.resourceCode)
                    }
                    if (this.userService.isLoggedIn()) {
                        this.handleShareAssignModal()
                    }
                    this.emitAdminHeaderLinks()
                } else {
                    this.titleService.setTitle(PBSLM_TITLE)
                }
            })
        )
    }

    public ngOnChanges() {
        this.premiumCollectionSlug = ResourceUtils.getPremiumCollectionSlug()
    }

    private handleNavigationOnPreview(): void {
        combineLatest([
            this.previewService.previewOnResource$,
            this.previewService.resource$,
            this.activatedRoute.queryParams,
        ])
            .pipe(
                takeUntil(this.destroyed$),
                tap(([isOnPreviewMode, resource, queryParams]: [boolean, Resource, Params]) => {
                    if (
                        isOnPreviewMode &&
                        !resource?.isAvailableToStudents &&
                        queryParams.student
                    ) {
                        this.router.navigate(["error"])
                    }
                })
            )
            .subscribe()
    }

    checkResourceSkipCautionState() {
        if (this.resource.opensAsMediaGallery || !this.resource.shouldShowContentWarnings) return
        this.indexedDBService
            .getDataForSingleResources()
            .pipe(
                takeUntil(this.destroyed$),
                tap((data: []) => this.checkAndDoCautionFlagsActions(data))
            )
            .subscribe()
    }

    checkAndDoCautionFlagsActions(data: []) {
        this.cautionFlagsService.isResourceCautionFlagSkipped(data, this.resource.resourceCode)
        this.cautionFlagDisplayObservable.subscribe()
    }

    public navigateToPreviousUrl() {
        this.resourceService.focusView = false
        if (this.previousUrl && Utils.isAddOnExperienceLink(this.previousUrl, "/add-on/search")) {
            const queryParams = this.previousRouteService.currentQueryParams$.value
            this.router.navigate(["/add-on/search"], {
                queryParams,
                queryParamsHandling: "merge",
            })
        }
    }

    scrollToCautionsTab() {
        const element: HTMLElement = document.getElementById("nav-tab")
        if (
            element &&
            this.resourceService.shouldShowCautionTab(this.resource.shouldShowContentWarnings)
        ) {
            // added in order to allow resource to be rendered in page, otherwise the scroll coordinates will be wrong
            asyncScheduler.schedule(() => {
                element.scrollIntoView({ behavior: "smooth" })
                this.resourceService.deleteActiveTab()
            }, 60)
        }
    }

    public openShareAssignModal() {
        this.modalService.open(SocialShareModalComponent, {
            data: {
                titleToShare: this.resource.title,
            },
        })
    }

    public isResourceMenuReady() {
        return this.resource.isLoaded
    }

    public hasStudentExperience() {
        return this.userService.hasStudentExperience()
    }

    public assignToGoogleClassroomClass() {
        if (!this.isAddonView) {
            return this.modalService.open(ShareLinkGoogleClassModalComponent, {
                data: { resource: this.resource },
            })
        }

        this.subscriptions.push(
            this.addOnService.createAttachment(this.resource.resourceCode).subscribe()
        )
    }

    public onOpenShareAssignModal() {
        this.openShareAssignModal()
    }

    public OpenShareLink() {
        this.modalService.open(ShareLinkModalComponent)
    }

    public buildALesson() {
        if (!this.userService.isLoggedIn()) {
            this.lmModalService.openLoginModal()
            return
        }

        this.lessonBuilderService.prefilledResource.next(this.resource)
        this.router.navigate(["/tools/lessonbuilder/new/"])
    }

    public isFavoriteDataReady() {
        return this.resource.favoriteId
    }

    public alignToTopOfAssetView() {
        return this.resource.mediaType !== "Audio"
    }

    public findPremiumCollectionOfResource(resource: Resource) {
        const previousURL = localStorage.getItem("previousURL")
        if (previousURL) {
            if (
                resource.collection.firstCollection &&
                [resource.collection.firstCollection, ...resource.collection.collections].find(
                    (obj) => previousURL.includes(obj.slug)
                )
            ) {
                this.premiumCollectionOfResource = PremiumCollectionOfResource.fromData(
                    [resource.collection.firstCollection, ...resource.collection.collections].find(
                        (obj) => previousURL.includes(obj.slug)
                    )
                )
            }
        } else if (this.premiumCollectionSlug) {
            if (
                resource.collection.firstCollection &&
                resource.collection.firstCollection.slug === this.premiumCollectionSlug
            ) {
                this.premiumCollectionOfResource = PremiumCollectionOfResource.fromData(
                    resource.collection.firstCollection
                )
            } else {
                const collection = resource.collection.collections.find(
                    (obj) => obj.slug === this.premiumCollectionSlug
                )
                if (collection && collection.style_content) {
                    this.premiumCollectionOfResource =
                        PremiumCollectionOfResource.fromData(collection)
                }
            }
        } else {
            if (resource.collection.firstCollection) {
                this.premiumCollectionOfResource = PremiumCollectionOfResource.fromData(
                    resource.collection.firstCollection
                )
            }
        }
    }

    public isLTI() {
        return !!this.resource.assets.find((asset) => {
            return asset.hasLTI()
        })
    }

    public canAssign() {
        return (
            this.resource.isAssignable &&
            !(this.resource.opensAsMediaGallery && this.resource.hasIlpAsset())
        )
    }

    private updateTooltips() {
        const canBuildLesson = this.canAssign()
        if (this.resource.isAvailableToStudents) {
            this.shareToGCTooltip = "Share to Google Classroom."
            this.shareWithStudentsTooltip = ""
        } else if (this.resource.shouldShowContentWarnings) {
            this.shareToGCTooltip = "This resource contains a content caution flag."
            if (canBuildLesson) {
                this.shareToGCTooltip +=
                    " Please review and use the lesson builder tool to share it to students."
            }
            this.shareWithStudentsTooltip = this.shareToGCTooltip
        } else {
            this.shareToGCTooltip =
                "This resource is for educators only and is not shareable with students."
            this.shareWithStudentsTooltip = this.shareToGCTooltip
        }

        if (!this.userService.isLoggedIn()) {
            this.buildLessonTooltip =
                "Sign in or create an account in order to use this resource in Lesson Builder"
        }

        if (!canBuildLesson) {
            this.buildLessonTooltip = "This resource can’t be used in Lesson Builder."
            if (this.resource.isAvailableToStudents) {
                this.buildLessonTooltip += " Try the Share link with students instead!"
            }
        }
    }

    private handleShareAssignModal() {
        const localStorageKey = this.resourceService.getLocalStorageShareAssignModalKey()
        if (window.localStorage.getItem(localStorageKey)) {
            window.localStorage.removeItem(localStorageKey)
            this.openShareAssignModal()
        }
    }

    private makeTitle(): string {
        let title
        if (this.resource.project && this.resource.project.title) {
            title = `${this.resource.title} | ${this.resource.project.title} | ${PBSLM_TITLE}`
        } else {
            title = `${this.resource.title} | ${PBSLM_TITLE}`
        }
        return this.truncatePipe.transform(title, 250)
    }

    private emitAdminHeaderLinks() {
        const { url: adminLink, title } = this.resource.adminLink

        this.userService.emitAdminHeaderData({
            title,
            adminLink,
        })
    }

    handleShareButtonsTooltipDisplay(resizeEvent: IWindowResizeEvent) {
        if (
            resizeEvent.innerWidth <= 800 ||
            (resizeEvent.innerWidth <= 1500 && resizeEvent.innerWidth >= 992)
        ) {
            this.shareButtonsPlacement = {
                innerWidth: resizeEvent.innerWidth,
                placement: "top-left",
            }
        } else {
            this.shareButtonsPlacement = null
        }
    }

    public ngOnDestroy() {
        this.globalStoreService.currentResource = null
        this.cautionFlagsService.cautionFlagDisplay = false
        this.userService.emitAdminHeaderData({})
        this.subscriptions.map((subscription: Subscription) => subscription.unsubscribe())
        this.destroyed$.next()
        this.destroyed$.complete()
    }
}
